import React, { useCallback, useState } from "react";
import { Alert } from "reactstrap";
import useHttp, { RequestConfig } from "../hooks/use-http";
import AppConstants from "../shared/contants";
import FormInput, { BaseFormInputState, FormCheckBoxListInput, FormListItem, FormListViewInput, FormRadioBoxListInput, FormSelectInput, FormTextAreaInput } from "../ui/FormInput";

export type FormValue = {
    firstName: BaseFormInputState<string | null>;
    lastName: BaseFormInputState<string | null>;
    userName: BaseFormInputState<string | null>;
    password: BaseFormInputState<string | null>;
    conformPassword: BaseFormInputState<string | null>;
    about: BaseFormInputState<string | null>;
    acceptTermCondition: BaseFormInputState<boolean>;
    gender: BaseFormInputState<string | null>;
    hobbies: BaseFormInputState<Array<string>>;
};

const Sample = () => {
    const [formFloating] = useState<boolean>(false)
    const [isFormSubmitted, setIsFormSubmitted] = useState<{ submitted: boolean, messages: string[], formData?: string }>({ submitted: false, messages: [], formData: undefined })
    const { status, error, sendRequest } = useHttp();

    const [formValues, setFormValues] = useState<FormValue>({
        firstName: { value: "Mark", isTouched: false, errors: [], isValid: true },
        lastName: { value: "Awasthi", isTouched: false, errors: [], isValid: true },
        userName: { value: "vinayavasthi", isTouched: false, errors: [], isValid: true },
        password: { value: "sample@123", isTouched: false, errors: [], isValid: true },
        conformPassword: { value: "sample@123", isTouched: false, errors: [], isValid: true },
        about: { value: "Lorem ipsum is placeholder text commonly used in the graphic, print, and publishing industries for previewing layouts and visual mockups.", isTouched: false, errors: [], isValid: true },
        acceptTermCondition: { value: false, isTouched: false, errors: [], isValid: true },
        gender: { value: "", isTouched: false, errors: [], isValid: true },
        hobbies: { value: [], isTouched: false, errors: [], isValid: true },
    })

    const [genders] = useState<FormListItem[]>([
        { title: "Male", value: "male", selected: false },
        { title: "Female", value: "female", selected: false },
    ])

    const [hobbies] = useState<FormListItem[]>([
        { title: "Sports", value: "sports", selected: false },
        { title: "Reading Books", value: "reading-books", selected: false },
        { title: "Writing Nobels", value: "writting-books", selected: false },
    ])

    const getFormInputClass = useCallback((className?: string) => {
        return `${formFloating ? "form-floating " : ""}${className} mb-2`
    }, [formFloating])

    const updateFormField = (
        fieldName: keyof FormValue,
        isValidField: boolean,
        errorMessage: string | string[],
        isTouched?: boolean
    ) => {
        const field = formValues[fieldName];
        const updatedField = {
            ...field,
            isValid: isValidField,
            isTouched: isTouched,
            errors: isValidField ? [] : Array.isArray(errorMessage) ? errorMessage : [errorMessage],
        };

        if (field.isValid !== updatedField.isValid) {
            setFormValues((prevStatus) => ({
                ...prevStatus,
                [fieldName]: updatedField,
            }));
        }
    };

    const isValidField = (field?: keyof typeof formValues) => {
        if (field) {
            const xfield = formValues[field];            
            if (xfield && xfield.errors.length > 0) {
                return false;
            }
        }
        

        if (field && field === "firstName") {
            const isValidField = !(
                !formValues.firstName ||
                !formValues.firstName.value ||
                formValues.firstName.value.length < 2 ||
                formValues.firstName.value.length > 15
            );

            updateFormField(field, isValidField, "A value is required, and its length should fall between 2 and 15 characters.", true);
            return isValidField;
        }

        if (field && field === "lastName") {
            let isValidField = !(
                !formValues.lastName ||
                !formValues.lastName.value ||
                formValues.lastName?.value.length < 2 ||
                formValues.lastName?.value.length > 15
            );

            updateFormField(field, isValidField, "A value is required, and its length should fall between 2 and 15 characters.", true);
            return isValidField;
        }

        if (field && field === "about") {
            let isValidField: boolean = !(
                !formValues.about ||
                !formValues.about.value ||
                formValues.about.value === "" ||
                formValues.about?.value.length > 300
            );            

            updateFormField(field, isValidField, "A value is required, and its length should not exceed 300 characters.", true);
            return isValidField;
        }

        if (field && field === "userName") {
            let isValidField: boolean = !(
                !formValues.userName ||
                !formValues.userName.value ||
                formValues.userName.value === "" ||
                formValues.userName?.value.length > 30);

            updateFormField(field, isValidField, "A value is required, and its length should fall between 3 and 30 characters.", true);
            return isValidField;
        }

        if (field && (field === "password" || field === "conformPassword")) {
            let isValidField: boolean = true;

            if (field && field === "password") {
                isValidField = !(
                    !formValues.password ||
                    !formValues.password.value ||
                    formValues.password.value === "" ||
                    formValues.password?.value.length > 30);

                updateFormField(field, isValidField, "A value is required, and its length should fall between 6 and 30 characters.", true);
                return isValidField;
            }

            if (field && field === "conformPassword") {
                isValidField = !(
                    !formValues.conformPassword ||
                    !formValues.conformPassword.value ||
                    formValues.conformPassword.value === "" ||
                    formValues.password.value !== formValues.conformPassword.value);

                updateFormField(field, isValidField, "A value is required, and it should be match with password.", true);
                return isValidField;
            }

            return isValidField;
        }

        if (field && field === "gender") {
            let isValidField = !(
                !formValues.gender ||
                !formValues.gender.value ||
                formValues.gender.value === "");

            updateFormField(field, isValidField, "A value is required.", true);
            return isValidField;
        }

        if (field && field === "hobbies") {
            let isValidField: boolean = !(
                !formValues.hobbies ||
                !formValues.hobbies?.value ||
                formValues.hobbies?.value.length < 2);
            updateFormField(field, isValidField, "A value is required, and need to select atleast two hobbies", true);
            return isValidField;
        }

        if (field && field === "acceptTermCondition") {
            let isValidField: boolean = (formValues.acceptTermCondition?.value ?? false);
            updateFormField(field, isValidField, "Please accept our term & conditions", true);
            return isValidField;
        }

        return false;
    }

    const isValidForm = () => {
        let isValid: boolean = true;
        if (!isValidField("firstName")) isValid = false;
        if (!isValidField("lastName")) isValid = false;
        if (!isValidField("userName")) isValid = false;
        if (!isValidField("password")) isValid = false;
        if (!isValidField("conformPassword")) isValid = false;
        if (!isValidField("about")) isValid = false;
        if (!isValidField("gender")) isValid = false;
        if (!isValidField("hobbies")) isValid = false;
        if (!isValidField("acceptTermCondition")) isValid = false;

        return isValid;
    };

    const handleInputChange = (field: keyof typeof formValues, value?: string | boolean | number | string[] | FormListItem[]) => {
        setFormValues((prevValues: FormValue) => ({
            ...prevValues,
            [field]: { value: value, isValid: true, isTouched: true, errors: [] },
        }));
    };

    const formSubmitHandler = (e: React.FormEvent) => {
        e.preventDefault();
        setIsFormSubmitted((prev) => ({ ...prev, submitted: false, formData: undefined, messages: [] }));
        if (isValidForm()) {
            const formData = {
                firstName: formValues.firstName.value,
                lastName: formValues.lastName.value,
                gender: formValues.gender.value,
                username: formValues.userName.value,
                password: formValues.password.value,
                conformPassword: formValues.conformPassword.value,
                about: formValues.about.value,
                hobbies: formValues.hobbies.value,
                acceptTermCondition: formValues.acceptTermCondition.value
            }

            const requestConfig: RequestConfig = {
                url: `${AppConstants.API_BASE_URL}/common/new-request`,
                method: "post",
                request_type: "json",
                body: formData
            }

            sendRequest(requestConfig, (data: { data: any, validation: { errors: Record<string, string[]> } }) => {
                console.log(data);
                if (data?.validation?.errors["about"]) {
                    updateFormField("about", false, data?.validation?.errors["about"], true)
                    setIsFormSubmitted((prev) => ({ ...prev, submitted: false, formData: undefined, messages: ["something went wrong!"] }));
                } else {
                    setIsFormSubmitted((prev) => ({ ...prev, submitted: true, formData: JSON.stringify(formData), messages: ["submitted, thank you"] }));
                }

                setTimeout(() => {
                    console.log(formValues.about);
                }, 1000)
            });
        }

        console.log("formValues:", formValues);
    }


    const formResetHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();

        setFormValues({
            firstName: { value: "", isTouched: false, errors: [], isValid: true },
            lastName: { value: "", isTouched: false, errors: [], isValid: true },
            userName: { value: "", isTouched: false, errors: [], isValid: true },
            password: { value: "", isTouched: false, errors: [], isValid: true },
            conformPassword: { value: "", isTouched: false, errors: [], isValid: true },
            about: { value: "", isTouched: false, errors: [], isValid: true },
            acceptTermCondition: { value: false, isTouched: false, errors: [], isValid: true },
            gender: { value: "", isTouched: false, errors: [], isValid: true },
            hobbies: { value: [], isTouched: false, errors: [], isValid: true },
        })

        setIsFormSubmitted({ submitted: false, formData: undefined, messages: [] })
        genders.forEach(x => { x.selected = false; })
        hobbies.forEach(x => { x.selected = false; })
    }

    return (
        <div className="app">
            <div className="container">
                <div className="row border">
                    <div className="col-12">
                        <div className="p-5 m-5">
                            {isFormSubmitted.submitted && (isFormSubmitted.messages.map((x) => { return <Alert>{x}</Alert> }))}
                            {isFormSubmitted.submitted && (<p>{isFormSubmitted.formData}</p>)}
                            <form
                                id="signupForm"
                                className={`${formFloating ? "form-floating " : ""} needs-validation ${!isValidForm ? "was-validated" : ""}`}
                                onSubmit={formSubmitHandler}
                                noValidate >
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-6")}`}>
                                        <FormInput
                                            label={"First name"}
                                            name="firmName"
                                            type="text"
                                            value={formValues.firstName?.value ?? ""}
                                            isValid={!formValues.firstName?.isTouched || formValues.about.errors.length === 0 || (formValues.firstName?.isTouched && isValidField("firstName"))}
                                            errors={formValues.firstName.errors}
                                            input={{
                                                onBlur: (e) => {
                                                    if (!formValues.firstName.isTouched) {
                                                        handleInputChange("firstName", e.target.value)
                                                    }
                                                }
                                            }}
                                            onChange={(e) => { handleInputChange("firstName", e.target.value) }}
                                            formFloating={formFloating} />
                                    </div>

                                    <div className={`${getFormInputClass("col-md-6")}`}>
                                        <FormInput
                                            label={"Last name"}
                                            name="lastName"
                                            type="text"
                                            value={formValues.lastName?.value ?? ""}
                                            isValid={!formValues.lastName?.isTouched || (formValues.lastName?.isTouched && isValidField("lastName"))}
                                            errors={formValues.lastName.errors}
                                            input={{
                                                onBlur: (e) => {
                                                    if (!formValues.lastName.isTouched) {
                                                        handleInputChange("lastName", e.target.value)
                                                    }
                                                },
                                            }}
                                            onChange={(e) => { handleInputChange("lastName", e.target.value) }}
                                            formFloating={formFloating} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <FormSelectInput
                                            name="gender"
                                            label="Select Gender"
                                            dataSource={genders}
                                            value={formValues.gender?.value ?? ""}
                                            isValid={!formValues.gender?.isTouched || (formValues.gender?.isTouched && isValidField("gender"))}
                                            errors={formValues.gender.errors}
                                            onChange={(e, data) => {
                                                //console.log(e.target.value, e.target)
                                                handleInputChange("gender", e.target.value)
                                                //setGenders(data ?? [])
                                            }}
                                            formFloating={formFloating} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <label className="form-label">Select Gender:&nbsp;</label>
                                        <FormRadioBoxListInput
                                            label="gender"
                                            name="gender"
                                            inline={true}
                                            dataSource={genders}
                                            value={formValues.gender?.value ?? ""}
                                            isValid={!formValues.gender?.isTouched || (formValues.gender?.isTouched && isValidField("gender"))}
                                            errors={formValues.gender.errors}
                                            onChange={(e, data) => {
                                                //console.log(e.target.value, e.target.checked)
                                                handleInputChange("gender", e.target.value)
                                                //setGenders(data ?? [])
                                            }}
                                            formFloating={formFloating}
                                        />

                                    </div>
                                </div>
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <FormInput
                                            label={"Username"}
                                            name="userName"
                                            type="text"
                                            value={formValues.userName?.value ?? ""}
                                            isValid={!formValues.userName?.isTouched || (formValues.userName?.isTouched && isValidField("userName"))}
                                            errors={formValues.userName.errors}
                                            input={{
                                                onBlur: (e) => {
                                                    if (!formValues.userName?.isTouched) {
                                                        handleInputChange("userName", e.target.value)
                                                    }
                                                }
                                            }}
                                            onChange={(e) => { handleInputChange("userName", e.target.value) }}
                                            formFloating={formFloating} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <FormInput
                                            label={"Password"}
                                            name="password"
                                            type="text"
                                            value={formValues.password?.value ?? ""}
                                            isValid={!formValues.password?.isTouched || (formValues.password?.isTouched && isValidField("password"))}
                                            errors={formValues.password.errors}
                                            input={{
                                                onBlur: (e) => {
                                                    if (!formValues.password?.isTouched) {
                                                        handleInputChange("password", e.target.value)
                                                    }
                                                }
                                            }}
                                            onChange={(e) => { handleInputChange("password", e.target.value) }}
                                            formFloating={formFloating} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <FormInput
                                            label={"Conform Password"}
                                            name="conform"
                                            type="text"
                                            value={formValues.conformPassword?.value ?? ""}
                                            isValid={!formValues.conformPassword?.isTouched || (formValues.conformPassword?.isTouched && isValidField("conformPassword"))}
                                            errors={formValues.conformPassword.errors}
                                            input={{
                                                onBlur: (e) => {
                                                    if (!formValues.conformPassword?.isTouched) {
                                                        handleInputChange("conformPassword", e.target.value)
                                                    }
                                                }
                                            }}
                                            onChange={(e) => { handleInputChange("conformPassword", e.target.value) }}
                                            formFloating={formFloating} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <FormTextAreaInput
                                            label={"About"}
                                            name="about"
                                            value={formValues.about?.value ?? ""}
                                            isValid={!formValues.about?.isTouched || (formValues.about?.isTouched && isValidField("about"))}
                                            errors={formValues.about.errors}
                                            input={{
                                                onBlur: (e) => {                                                    
                                                    if (!formValues.about?.isTouched || formValues.about.errors.length > 0) {
                                                        handleInputChange("about", e.target.value)
                                                    }
                                                }
                                            }}
                                            onChange={(e) => { handleInputChange("about", e.target.value) }}
                                            formFloating={formFloating} />                                        
                                    </div>
                                </div>


                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <label className="form-label">Select hobbies:&nbsp;</label>
                                        <FormCheckBoxListInput
                                            label="hobbies"
                                            name="hobbies"
                                            inline={true}
                                            dataSource={hobbies}
                                            value={formValues.hobbies?.value}
                                            isValid={!formValues.hobbies?.isTouched || (formValues.hobbies?.isTouched && isValidField("hobbies"))}
                                            errors={formValues.hobbies.errors}
                                            onChange={(e, data) => {
                                                //console.log(e.target.value, e.target.checked)
                                                let selectedHobbies = data?.filter(x => x.selected).map(x => {
                                                    return String(x.value ?? x.title)
                                                })
                                                handleInputChange("hobbies", selectedHobbies ?? [])
                                                //setHobbies(data ?? [])
                                            }}
                                        />

                                    </div>
                                </div>

                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")}`}>
                                        <FormListViewInput
                                            label="Select hobbies"
                                            name="hobbies"
                                            value={formValues.hobbies?.value}
                                            maxItem={2}
                                            dataSource={hobbies}
                                            isValid={!formValues.hobbies?.isTouched || (formValues.hobbies?.isTouched && isValidField("hobbies"))}
                                            errors={formValues.hobbies.errors}
                                            onChange={(e, data) => {
                                                //console.log(e.target.value, e.target.checked)
                                                let selectedHobbies = data?.filter(x => x.selected).map(x => {
                                                    return String(x.value ?? x.title)
                                                })
                                                handleInputChange("hobbies", selectedHobbies ?? [])
                                                //setHobbies(data ?? [])
                                            }}
                                            formFloating={formFloating}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className={`${getFormInputClass("col-md-12")} mt-2`}>
                                        <FormInput
                                            label={"accept term & condition"}
                                            name="acceptTermCondition"
                                            type="checkbox"
                                            value="acceptTermCondition"
                                            checked={formValues.acceptTermCondition?.value}
                                            isValid={!formValues.acceptTermCondition?.isTouched || (formValues.acceptTermCondition?.isTouched && isValidField("acceptTermCondition"))}
                                            errors={formValues.acceptTermCondition.errors}
                                            onChange={(e, data) => {
                                                handleInputChange("acceptTermCondition", data?.checked ?? false)
                                            }}
                                            formFloating={formFloating} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className={`${formFloating ? "form-floating " : ""}col-md-12 mb-2`}>
                                        <div className="d-grid gap-2 d-md-flex justify-content-md-start" role="group">
                                            <button className="btn btn-primary" type="submit">Submit</button>
                                            <button className="btn btn-secondary" type="reset" onClick={(e) => formResetHandler(e)}>Reset</button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sample;

